import type { ComponentType } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import BandLab from './lab/BandLab'
import BeakerLab from './lab/BeakerLab'
import BoatLab from './lab/BoatLab'
import BookLab from './lab/BookLab'
import BowlsLab from './lab/BowlsLab'
import BrownLab from './lab/BrownLab'
import CartLab from './lab/CartLab'
import CenterLab from './lab/CenterLab'
import CoinLab from './lab/CoinLab'
import CupScaleLab from './lab/CupScaleLab'
import DeformLab from './lab/DeformLab'
import DiffusionLab from './lab/DiffusionLab'
import DigitsLab from './lab/DigitsLab'
import DivisionLab from './lab/DivisionLab'
import DynamometerLab from './lab/DynamometerLab'
import EarthLab from './lab/EarthLab'
import ElevatorLab from './lab/ElevatorLab'
import FallLab from './lab/FallLab'
import FrictionLab from './lab/FrictionLab'
import IllusionLab from './lab/IllusionLab'
import LengthLab from './lab/LengthLab'
import LightAgeLab from './lab/LightAgeLab'
import LoadLab from './lab/LoadLab'
import MixLab from './lab/MixLab'
import OverflowLab from './lab/OverflowLab'
import PaletteLab from './lab/PaletteLab'
import ParallaxLab from './lab/ParallaxLab'
import PhenomenaLab from './lab/PhenomenaLab'
import PiLab from './lab/PiLab'
import PolarizerLab from './lab/PolarizerLab'
import PrefixLab from './lab/PrefixLab'
import RingLab from './lab/RingLab'
import RulerLab from './lab/RulerLab'
import ScaleLab from './lab/ScaleLab'
import ScalesLab from './lab/ScalesLab'
import SmellLab from './lab/SmellLab'
import SpeedLab from './lab/SpeedLab'
import SpinLab from './lab/SpinLab'
import SumLab from './lab/SumLab'
import SyringeLab from './lab/SyringeLab'
import UnitsLab from './lab/UnitsLab'
import VernierLab from './lab/VernierLab'
import WorldsLab from './lab/WorldsLab'

/**
 * 🧪 Лаборатория — опыты с ползунками (CLAUDE.md §8): ученик сам меняет массу, жёсткость,
 * ускорение и видит, как отвечают силы. Опыты — из карточек тем
 * (data/content/teoriya/kartochki/), физика — lib/physics.ts с тестами.
 *
 * Исключение из режима показа (§2.16, с 29.10.2026 по решению владельца). Открывается и
 * без входа: бот ведёт сюда кнопкой мини-приложения Telegram (handlers/nav_menu.py).
 * Работает целиком в браузере, к API не обращается. Адрес опыта — /lab/<id>.
 */

interface Опыт {
  id: string
  название: string
  кратко: string
  экран: ComponentType
}

const ТЕМЫ: { тема: string; опыты: Опыт[] }[] = [
  {
    тема: 'Введение',
    опыты: [
      { id: 'phenomena', название: 'Какое это явление?', кратко: 'механическое, тепловое, световое… и молния', экран: PhenomenaLab },
      { id: 'boat', название: 'Лодочка на слюде', кратко: 'горячая лодочка раскачивается сама', экран: BoatLab },
      { id: 'polarizer', название: 'Два поляроида', кратко: 'повернул фильтр — и свет погас', экран: PolarizerLab },
      { id: 'illusion', название: 'Обман зрения', кратко: 'почему в физике измеряют, а не верят глазам', экран: IllusionLab },
      { id: 'bowls', название: 'Три миски', кратко: 'руки «врут» о температуре, термометр — нет', экран: BowlsLab },
      { id: 'units', название: 'Чем измерить?', кратко: 'пяди, карандаши, попугаи и метр', экран: UnitsLab },
      { id: 'scale', название: 'Лестница масштабов', кратко: 'от атома до Солнца: приставки к метру', экран: ScaleLab },
      { id: 'spin', название: 'Балерина и Земля', кратко: 'почему секунду задают атомные часы', экран: SpinLab },
    ],
  },
  {
    тема: 'Измерения: шкала, площадь, объём',
    опыты: [
      { id: 'division', название: 'Цена деления любой шкалы', кратко: 'термометр, шприц, мензурка, линейка', экран: DivisionLab },
      { id: 'vernier', название: 'Нониус', кратко: 'как штангенциркуль меряет десятые миллиметра', экран: VernierLab },
      { id: 'pi', название: 'Откуда берётся π', кратко: 'колесо катится один оборот', экран: PiLab },
      { id: 'palette', название: 'Палетка', кратко: 'площадь кляксы по клеткам', экран: PaletteLab },
      { id: 'beaker', название: 'Объём погружением', кратко: 'мензурка, мениск и V₂ − V₁', экран: BeakerLab },
      { id: 'overflow', название: 'Отливной сосуд', кратко: 'тело не лезет в мензурку', экран: OverflowLab },
      { id: 'coin', название: 'Одна монета — четыре прибора', кратко: 'лента, линейка, штангенциркуль, микрометр', экран: CoinLab },
      { id: 'digits', название: 'Значащие цифры', кратко: 'точность — не знаки после запятой', экран: DigitsLab },
      { id: 'cup', название: 'Своя мензурка', кратко: 'стаканчик, шприц и метки через 20 мл', экран: CupScaleLab },
      { id: 'parallax', название: 'Глаз на уровне воды', кратко: 'почему отсчёт врёт, если смотреть сверху', экран: ParallaxLab },
    ],
  },
  {
    тема: 'Единицы и масштабы мира',
    опыты: [
      { id: 'prefix', название: 'Приставка — это множитель', кратко: 'мм, см², дм³ — в основные единицы', экран: PrefixLab },
      { id: 'speed', название: 'Метры в секунду и километры в час', кратко: 'откуда берётся 3,6', экран: SpeedLab },
      { id: 'worlds', название: 'Три мира', кратко: 'микро-, макро- и мегамир на одной шкале', экран: WorldsLab },
      { id: 'light', название: 'Свет из прошлого', кратко: 'почему далёкие звёзды мы видим такими, какими они были', экран: LightAgeLab },
    ],
  },
  {
    тема: 'Атомы и молекулы',
    опыты: [
      { id: 'syringe', название: 'Сжимаем шприц', кратко: 'воздух сжимается, вода — почти нет', экран: SyringeLab },
      { id: 'ring', название: 'Шар и кольцо', кратко: 'нагретый шар застревает в кольце', экран: RingLab },
      { id: 'mix', название: 'Вода и спирт', кратко: '100 мл + 100 мл — меньше 200 мл', экран: MixLab },
    ],
  },
  {
    тема: 'Диффузия и броуновское движение',
    опыты: [
      { id: 'diffusion', название: 'Диффузия в газе, жидкости и твёрдом теле', кратко: 'вещества сами проникают друг в друга', экран: DiffusionLab },
      { id: 'brown', название: 'Броуновская частица', кратко: 'крупинку краски толкают молекулы воды', экран: BrownLab },
      { id: 'smell', название: 'Почему запах идёт минуты', кратко: 'молекула летит 500 м/с, но зигзагом', экран: SmellLab },
    ],
  },
  {
    тема: 'Сила тяжести',
    опыты: [
      { id: 'fall', название: 'Лист и блокнот', кратко: 'что падает быстрее и при чём тут воздух', экран: FallLab },
      { id: 'center', название: 'Где центр тяжести', кратко: 'подвесь пластину за разные точки', экран: CenterLab },
      { id: 'earth', название: 'Вращающаяся Земля', кратко: 'почему g на полюсе больше', экран: EarthLab },
    ],
  },
  {
    тема: 'Сила упругости',
    опыты: [
      { id: 'ruler', название: 'Гиря на линейке', кратко: 'почему гиря не падает', экран: RulerLab },
      { id: 'band', название: 'Закон Гука', кратко: 'резиновая нить и грузы', экран: BandLab },
      { id: 'deform', название: 'Виды деформаций', кратко: 'растяжение, сжатие, изгиб, сдвиг, кручение', экран: DeformLab },
    ],
  },
  {
    тема: 'Измерение силы',
    опыты: [
      { id: 'dynamometer', название: 'Динамометр', кратко: 'предел измерения и цена деления', экран: DynamometerLab },
      { id: 'length', название: 'Длина или удлинение', кратко: 'главная ловушка графиков', экран: LengthLab },
      { id: 'scales', название: 'Весы на Земле и Луне', кратко: 'рычажные против пружинных', экран: ScalesLab },
    ],
  },
  {
    тема: 'Вес тела',
    опыты: [
      { id: 'elevator', название: 'Вес в лифте', кратко: 'перегрузка и невесомость', экран: ElevatorLab },
      { id: 'book', название: 'Книга на столе', кратко: 'к чему приложена каждая сила', экран: BookLab },
    ],
  },
  {
    тема: 'Сложение сил',
    опыты: [
      { id: 'sum', название: 'Сложение сил на динамометре', кратко: 'в одну сторону — сумма, в разные — разность', экран: SumLab },
      { id: 'cart', название: 'Тележку тянут и толкают', кратко: 'одна сила вместо двух', экран: CartLab },
    ],
  },
  {
    тема: 'Сила трения',
    опыты: [
      { id: 'friction', название: 'От покоя к скольжению', кратко: 'трение покоя растёт, трение скольжения — нет', экран: FrictionLab },
      { id: 'load', название: 'Догружаем брусок', кратко: 'коэффициент трения из трёх замеров', экран: LoadLab },
    ],
  },
]

const ВСЕ = ТЕМЫ.flatMap((t) => t.опыты)

/** loggedIn — ученик вошёл на сайт: тогда внизу есть путь на главную. */
export default function Lab({ loggedIn = true }: { loggedIn?: boolean }) {
  const navigate = useNavigate()
  const { opyt } = useParams()
  const опыт = ВСЕ.find((o) => o.id === opyt)

  if (опыт) {
    const Экран = опыт.экран
    return (
      <>
        <button className="btn lab-back" onClick={() => navigate('/lab')}>← Все опыты</button>
        <h1>{опыт.название}</h1>
        <Экран />
      </>
    )
  }

  return (
    <>
      <h1>🧪 Лаборатория</h1>
      <p className="muted small" style={{ marginTop: -8 }}>
        Опыты с ползунками: сначала предскажи, что будет, потом проверь.
      </p>
      {ТЕМЫ.map((t) => (
        <section key={t.тема} className="lab-menu-group">
          <h2 className="lab-menu-title">{t.тема}</h2>
          <div className="lab-menu">
            {t.опыты.map((o) => (
              <button key={o.id} className="list-item lab-menu-item" onClick={() => navigate(`/lab/${o.id}`)}>
                <b>{o.название}</b>
                <span className="muted small">{o.кратко}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
      {loggedIn && (
        <div className="row-wrap" style={{ marginTop: 16 }}>
          <button className="btn" onClick={() => navigate('/')}>🏠 Главная</button>
        </div>
      )}
    </>
  )
}
